import {
  Box,
  Container,
  Icon,
  Stack,
  Typography,
  useMediaQuery,
} from '@ace-clublabs/shared-component-library';
import { useTreatment } from '@splitsoftware/splitio-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LoadingContainer from '@/components/shared/LoadingContainer';
import ProgressBar from '@/components/shared/ProgressBar';
import { constants } from '@/constants';
import useContextAwarenessParams from '@/hooks/useContextAwarenessParams';
import useLandingPageSetup from '@/hooks/useLandingPageSetup';
import useProgress from '@/hooks/useProgress';
import { Page } from '@/lib/tagging';
import useTagging from '@/lib/tagging/useTagging';
import useAppointmentStore from '@/store/AppointmentStore';
import useProgressStore from '@/store/ProgressStore';
import useSessionStore from '@/store/SessionStore';
import { FlowType } from '@/types/FlowType';
import RSSearchBox from '../components/RSSearchBox';
import RSSearchBoxMobile from '../components/RSSearchBoxMobile';
import { useRSSearchBox } from '../hooks/useRSSearchBox';

interface RelevanceSearchProps {
  flow: FlowType;
}

const RELEVANCE_PROGRESS_PERCENT = 15;
const RelevanceSearch: React.FC<RelevanceSearchProps> = ({ flow }) => {
  const [tagged, setTagged] = useState(false);

  const { treatment, isReady } = useTreatment({
    name: 'travel_scheduler_AA_test',
  });

  const navigate = useNavigate();
  const isEmailFlow = flow === FlowType.EMAIL;

  const searchedDestination = useAppointmentStore(
    (state) => state.searchedDestination,
  );

  const { userSearch } = useAppointmentStore();

  const { sessionId, sessionSource, flowType } = useSessionStore();
  const tagging = useTagging();

  const { isResolving } = useContextAwarenessParams(flow);
  const { proceedWithDestination, destinationsQuery } =
    useLandingPageSetup(flow);
  useProgress(RELEVANCE_PROGRESS_PERCENT);

  const isDestinationsLoading =
    isResolving ||
    destinationsQuery.isPending ||
    destinationsQuery.error?.message === constants.TANSTACK_ABORTED_ERROR;

  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const setShowProgress = useProgressStore((state) => state.setShowProgress);

  const { searchBoxProps, isSearchPanelOpen, handleClickContinue } =
    useRSSearchBox({
      flow,
      proceedWithDestination,
    });

  useEffect(() => {
    if (!isMobileView) {
      setShowProgress(true);
      return;
    }

    setShowProgress(!isSearchPanelOpen);
  }, [isMobileView, isSearchPanelOpen, setShowProgress]);

  useEffect(() => {
    return () => setShowProgress(true);
  }, [setShowProgress]);

  useEffect(() => {
    if (!tagged && sessionSource && sessionId) {
      const trvEndLocation = searchedDestination || userSearch || '';
      const commPref = isEmailFlow ? 'email' : undefined;

      const experimentFlags =
        isReady && treatment ? `travel_scheduler_AA_test:${treatment}` : '';

      tagging.tag('view', sessionSource || 'new', {
        page: Page.destination,
        sessionid: sessionId,
        flow: flowType || '',
        trv_end_location: trvEndLocation,
        ...(commPref && { comm_pref: commPref }),
        ...(experimentFlags && { experiment_flags: experimentFlags }),
      });

      setTagged(true);
    }
  }, [
    sessionId,
    flowType,
    sessionSource,
    tagging.tag,
    tagged,
    searchedDestination,
    userSearch,
    isEmailFlow,
    treatment,
    isReady,
  ]);

  // Always return to the HelpSelection screen for the active flow, even when
  // the search route (/search) was reached via refresh or
  const handleClickBack = useCallback(() => {
    navigate('/', {
      replace: true,
      state: { direction: 'back' },
    });
  }, [navigate]);

  if (isDestinationsLoading) {
    const loadingMessage = isEmailFlow
      ? 'Your advisor app is loading...'
      : 'Your new adventure awaits...';
    return <LoadingContainer message={loadingMessage} showRadios={false} />;
  }

  return (
    <Stack
      direction='column'
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        textAlign: 'start',
        backgroundColor: '#F7F5F1',
      }}
      data-testid='relevance-search-page'
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          backgroundColor: '#222222',
          py: 4,
        }}
        data-testid='relevance-search-hero-section'
      >
        <Box
          component='video'
          // @ts-expect-error
          src={constants.RELEVANCE_SEARCH_BG_VIDEO_URL}
          autoPlay
          loop
          muted
          playsInline
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            zIndex: 0,
          }}
          data-testid='relevance-search-hero-video'
        />

        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(10,10,10,0.4)',
            zIndex: 1,
          }}
          aria-hidden
          data-testid='relevance-search-hero-overlay'
        />
        <Container
          maxWidth='lg'
          sx={{
            zIndex: 2,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          data-testid='relevance-search-hero-container'
        >
          <Stack
            direction='column'
            spacing={4}
            sx={{
              width: '100%',
              px: { md: 6, xs: 2 },
              pt: 6,
              pb: 5,
              borderRadius: 2,
              alignItems: 'center',
              zIndex: 3,
            }}
            data-testid='relevance-search-hero-stack'
          >
            <Box data-testid='relevance-search-hero-logo'>
              <Icon
                iconName='ace-orbit-logo'
                sx={{
                  color: '#fff',
                  width: 85,
                  height: 52,
                  '& path': {
                    fill: 'white',
                  },
                }}
              />
            </Box>
            <Typography
              variant='h3'
              sx={{ color: '#fff', textAlign: 'center' }}
              data-testid='relevance-search-hero-title'
            >
              {isEmailFlow
                ? 'Email a Travel Advisor'
                : 'Make an appointment with a travel advisor to plan and book travel'}
            </Typography>
            {isMobileView ? (
              <RSSearchBoxMobile {...searchBoxProps} />
            ) : (
              <RSSearchBox {...searchBoxProps} sx={{ width: '85%' }} />
            )}
          </Stack>
        </Container>
      </Box>
      <ProgressBar
        displayBackButton={!isEmailFlow}
        onClickContinue={handleClickContinue}
        onClickBack={handleClickBack}
        disableNavigation={true}
      />
    </Stack>
  );
};

export default RelevanceSearch;
